import type { PersistStorage, StorageValue } from "zustand/middleware";

const LARGEST_KEY_REPORT_COUNT = 5;

const describeLocalStorageUsage = (): Array<[string, number]> => {
  try {
    return Object.keys(localStorage)
      .map<[string, number]>((key) => [key, localStorage.getItem(key)?.length ?? 0])
      .sort((a, b) => b[1] - a[1])
      .slice(0, LARGEST_KEY_REPORT_COUNT);
  } catch {
    return [];
  }
};

const isSameSlice = <S extends object>(prev: S | null, next: S): boolean => {
  if (prev === null) return false;
  const prevKeys = Object.keys(prev) as Array<keyof S>;
  const nextKeys = Object.keys(next) as Array<keyof S>;
  return (
    prevKeys.length === nextKeys.length &&
    nextKeys.every((key) => prev[key] === next[key])
  );
};

/**
 * localStorage 기반 Zustand persist storage.
 *
 * - 쓰기 실패(QuotaExceededError 등)를 호출자에게 전파하지 않는다. 메모리 상태는 이미
 *   갱신됐으므로 화면 동작은 계속되고, 마지막으로 정상 저장된 값이 localStorage에 남는다.
 * - 저장 슬라이스의 최상위 값이 참조 동일하면 직렬화·쓰기를 생략한다. 저장 대상이 아닌
 *   UI 상태만 바뀐 setState는 localStorage를 건드리지 않는다.
 * - 쓰기에 실패한 최신 값은 기억했다가 다음 쓰기 또는 페이지 이탈·탭 숨김 때 다시 쓴다.
 *   다음 변경 없이 새로고침·탭 닫기가 일어나도 복원 기록이 화면보다 뒤처진 채 남지 않는다.
 * - 읽기·파싱 실패는 null로 처리해 store가 초기 상태로 시작하게 한다.
 */
export const createSafeLocalStorage = <S extends object>(): PersistStorage<S> => {
  let lastWrittenState: S | null = null;
  let pendingWrite: { name: string; value: StorageValue<S> } | null = null;

  const write = (name: string, value: StorageValue<S>): void => {
    if (isSameSlice(lastWrittenState, value.state)) return;
    const serialized = JSON.stringify(value);
    try {
      localStorage.setItem(name, serialized);
      lastWrittenState = value.state;
      pendingWrite = null;
    } catch (error) {
      pendingWrite = { name, value };
      console.warn(`[persist] "${name}" write skipped; keeping last saved value.`, {
        error,
        attemptedChars: serialized.length,
        largestKeys: describeLocalStorageUsage(),
      });
    }
  };

  const retryPendingWrite = (): void => {
    if (pendingWrite) write(pendingWrite.name, pendingWrite.value);
  };

  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", retryPendingWrite);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") retryPendingWrite();
    });
  }

  return {
    getItem: (name) => {
      try {
        const raw = localStorage.getItem(name);
        return raw ? (JSON.parse(raw) as StorageValue<S>) : null;
      } catch {
        return null;
      }
    },
    setItem: write,
    removeItem: (name) => {
      try {
        localStorage.removeItem(name);
      } catch {
        // ignore
      }
      lastWrittenState = null;
      pendingWrite = null;
    },
  };
};
